import { TuiMapperPipe } from '@taiga-ui/cdk';
import { TuiBadge } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Signal,
} from '@angular/core';
import { TuiHint } from '@taiga-ui/core';
import { RequestProduct } from '@core/types/RequestProduct';
import { ProductIconsNames, RequestProductIconSize } from './types';
import { TranslatePipe } from '@ngx-translate/core';
import { EntityTranslatePipe } from '@core/pipes';
import { RequestDevice } from '@core/types';
import { getRequestProductIconName } from './request-product-icon.helpers';
import { SvgIconComponent } from '@common/components/svg-icon/svg-icon.component';

type State = 'not-completed' | 'completed' | 'closed-forcibly' | 'has-device';

@Component({
  selector: 'x-request-product-icon',
  imports: [
    SvgIconComponent,
    TuiBadge,
    TuiHint,
    TranslatePipe,
    EntityTranslatePipe,
    TuiMapperPipe,
  ],
  templateUrl: './request-product-icon.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestProductIconComponent {
  readonly RequestProductIconSize = RequestProductIconSize;
  readonly $requestProduct = input.required<RequestProduct>({
    alias: 'requestProduct',
  });
  readonly $requestDevices = input.required<RequestDevice[]>({
    alias: 'requestDevices',
  });
  readonly $iconSize = input<RequestProductIconSize>(
    RequestProductIconSize.Md,
    { alias: 'iconSize' },
  );
  readonly $withCounter = input<boolean>(false, { alias: 'withCounter' });
  readonly $withoutPadding = input<boolean>(false, {
    alias: 'withoutPadding',
  });
  readonly $manualCounterScale = input<number>(0, {
    alias: 'manualCounterScale',
  });

  protected readonly $installedDevices: Signal<RequestDevice[]> = computed(() =>
    this.$requestDevices().filter(
      (device: RequestDevice) =>
        device.deviceModelId ===
        this.$requestProduct().requestProductId.deviceModel,
    ),
  );

  protected readonly $state: Signal<State> = computed(() => {
    const { completed, isClosedForcingly } = this.$requestProduct();

    if (isClosedForcingly) {
      return 'closed-forcibly';
    }

    if (completed) {
      return 'completed';
    }

    if (this.$installedDevices().length >= this.$requestProduct().amount) {
      return 'has-device';
    }

    return 'not-completed';
  });

  protected readonly $counterScale: Signal<number> = computed(() => {
    if (this.$manualCounterScale()) {
      return this.$manualCounterScale();
    }

    switch (this.$iconSize()) {
      case RequestProductIconSize.Xl:
        return 1.25;
      case RequestProductIconSize.Md:
      case RequestProductIconSize.Sm:
        return 0.9;
      case RequestProductIconSize.Lg:
      default:
        return 1;
    }
  });

  protected readonly $requestProductIconName: Signal<ProductIconsNames> =
    computed(() => {
      return getRequestProductIconName(
        this.$requestProduct().requestProductId.id,
      );
    });

  protected readonly $isCompleted = computed(
    () =>
      this.$requestProduct().completed &&
      !this.$requestProduct().isClosedForcingly,
  );

  joinNames(devices: RequestDevice[]): string {
    return devices.map(({ name }) => name).join(', ');
  }
}
